// Auth-endpoint client for the sign in / forgot / reset screens.
//
// These calls deliberately use native fetch and NOT the shared apiFetch:
// apiFetch treats any 401 as an expired session and redirects to /login while
// clearing the token. Devise returns 401 on wrong credentials, so routing the
// sign-in through apiFetch would turn a bad password into a redirect loop that
// swallows the error message. Here a non-2xx response is returned to the caller
// to branch on, never thrown.
function getCsrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  );
}

const jsonHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
  "X-CSRF-Token": getCsrfToken(),
});

async function request(path, method, body) {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  return {
    status: response.status,
    data,
    authorization: response.headers.get("Authorization"),
  };
}

export async function signIn({
  email,
  password,
  otpCode,
  rememberMe,
  rememberMeToken,
  locale,
}) {
  return request("/users/sign_in", "POST", {
    locale,
    user: {
      email,
      password,
      otp_code: otpCode,
      remember_me: rememberMe,
      remember_me_token: rememberMeToken,
    },
  });
}

export async function sendResetPasswordInstructions({ email, locale }) {
  const { data } = await request("/users/password", "POST", { locale, email });
  return data;
}

export async function resetPassword({
  password,
  passwordConfirmation,
  token,
  locale,
}) {
  const { data } = await request("/users/password", "PUT", {
    locale,
    password,
    password_confirmation: passwordConfirmation,
    token,
  });
  return data;
}

export async function signOut() {
  const { data } = await request("/users/sign_out", "DELETE", {});
  return data;
}

// Unauthenticated by design: the reset screen calls this before any session
// exists, so it lives here with the other pre-auth endpoints instead of
// apiFetch. Responds { valid: boolean }.
export async function validateResetToken(token) {
  const { data } = await request(
    `/api/users/validate_reset_token?token=${encodeURIComponent(token)}`,
    "GET"
  );
  return data;
}
